import $ from 'jquery';

/**
 * A component that manages the the flow of data into a form field (see example for expected HTML structure).
 *
 * Complex JS components that are used in forms (like autocompletes, imagefields, etc.) should inherit
 * from this component to ensure a consistent API across form elements.
 *
 * @param {Object} settings
 * @param {jQuery} settings.$el The jQuery element backing the FormFieldComponent
 * @param {Object} settings.data The data used to render the DOM
 * @param {string} settings.name The name of the field that this FormFieldComponent is for
 *
 * @class FormFieldComponent
 * @constructs FormFieldComponent
 *
 * @example
 * <div class="form-group js-form-group email">
 *   <label class="control-label" for="id_email">Email Address</label>
 *   <div class="controls">
 *     <input type="email" name="email" placeholder="Email Address" class="form-control" id="id_email" autocomplete="email" autocorrect="off" required="">
 *   </div>
 *   <div class="help-block js-errors"></div>
 * </div>
 */
const FormFieldComponent = function FormFieldComponent(settings) {
    $.extend(
        {
            $el: this.$el,
            data: {},
            name: this.name,
        },
        settings,
    );

    this.$el = settings.$el;
    this.$groupEl = this.$el.closest('.js-form-group');
    this.name = settings.name;

    this.data = { ...settings.data, name: this.name };

    this.bind();
};

/**
 * Marshals a list of messages into a jQuery object that can be displayed.
 *
 * @param {Array.<Message>} data The list of messages to marshal
 * @param {string=} itemClasses Optional string to use as classes on each error list item
 *
 * @returns {jQuery} The rendered message list
 */
FormFieldComponent.marshalMessageList = function marshalMessageList(data, itemClasses) {
    const $shell = $('<ul></ul>', { class: 'errorlist' });
    for (let i = 0; i < data.length; i += 1) {
        $shell.append(
            $('<li></li>', {
                class: itemClasses,
                text: data[i].display,
            }),
        );
    }

    return $shell.children().length ? $shell : '';
};

/**
 * The jQuery element that this data is tied to
 *
 * @type {jQuery}
 */
FormFieldComponent.prototype.$el = null;

/**
 * The .js-form-group element wrapping the field
 *
 * @type {jQuery}
 */
FormFieldComponent.prototype.$groupEl = null;

/**
 * Object that contains all of the data needed to render this field
 *
 * @type {Object}
 * @example
 * {
 *   choices: [
 *     {value: 'F'},
 *     {value: 'W'},
 *     {value: 'S'},
 *     {display: '$5-$33', value: 'F'},
 *   ],
 *   errors: [
 *      {'display': 'This field is required.'}
 *   ],
 *   is_readonly: false, // absence of this field means it is not read-only
 *   is_required: true,   // absence of this field means it is not required
 *   label: 'Shipping Options', // custom label to display; supersedes  what is set in a Django form
 *   name: 'shipping_type_code', // the name of the form field; useful for debugging
 *   suggestion: {value: 'F'}, // used to indicate this is the preferred value; only present when choices is present
 *   value: 'S', // can also be an array depending on the field; value: [{value: 'F'}, {value: 'W'}]
 * }
 */
FormFieldComponent.prototype.data = null;

/**
 * The name of the form field
 *
 * @type {string}
 */
FormFieldComponent.prototype.name = '';

/**
 * Clears all errors from the field
 */
FormFieldComponent.prototype.clearErrors = function clearErrors() {
    this.setErrors([]);
};

/**
 * @returns {string} The display text (if any) or the actual value
 */
FormFieldComponent.prototype.getDisplayValue = function getDisplayValue() {
    return this.data.display || this.data.value || '';
};

/**
 * @returns {Array.<Object>} Collection of error objects
 */
FormFieldComponent.prototype.getErrors = function getErrors() {
    return this.data.errors || [];
};

/**
 * @returns {jQuery} The jQuery element representing this form field
 */
FormFieldComponent.prototype.getEl = function getEl() {
    return this.$el;
};

/**
 * @returns {string} The name of the form field
 */
FormFieldComponent.prototype.getName = function getName() {
    return this.name;
};

/**
 * @returns {Array|number|Object|string} The field's value
 */
FormFieldComponent.prototype.getValue = function getValue() {
    return this.data.value;
};

/**
 * @returns {boolean} True indicates the field has at least one error
 */
FormFieldComponent.prototype.hasErrors = function hasErrors() {
    return this.getErrors().length;
};

/**
 * @returns {boolean} True indicates this field is required
 */
FormFieldComponent.prototype.isRequired = function isRequired() {
    return !!this.data.is_required;
};

/**
 * @returns {boolean} True indicates this field is error free and has a value, or is error free and
 *   doesn't require a value. Complex form fields should override this function if needed.
 */
FormFieldComponent.prototype.isValid = function isValid() {
    return !this.hasErrors() && (this.getValue() != null || !this.isRequired());
};

/**
 * Renders the form field's choices; defaults to rendering new <option> elements.
 * Complex form fields should override this function if needed.
 */
FormFieldComponent.prototype.renderChoices = function renderChoices() {
    const { choices } = this.data;
    if (choices) {
        const choiceList = [];
        $.each(choices, (i) => {
            const choice = choices[i];
            choiceList.push(
                $('<option></option>', {
                    text: choice.display || choice.value,
                    value: choice.value,
                }),
            );
        });
        this.$el.html(choiceList);
    }
};

/**
 * Renders all of the form field's errors.
 * Complex form fields should override this function if needed.
 *
 * @param {Array.<Object>=} errors Optional collection of specific errors to render
 */
FormFieldComponent.prototype.renderErrors = function renderErrors(errors) {
    const errorsToRender = errors || this.getErrors();
    let $errors = '';
    let shouldHaveErrorClass = false;
    if (errorsToRender?.length) {
        $errors = FormFieldComponent.marshalMessageList(errorsToRender);
        shouldHaveErrorClass = true;
    }

    this.$groupEl.toggleClass('has-error', shouldHaveErrorClass);
    this.$groupEl.find('.js-errors').html($errors);
};

/**
 * Renders the form field's non-review errors.
 * Complex form fields should override this function if needed.
 */
FormFieldComponent.prototype.renderNonReviewErrors = function renderNonReviewErrors() {
    const errors = [];
    $.each(this.getErrors(), (_, error) => {
        if (!error.is_review) {
            errors.push(error);
        }
    });
    this.renderErrors(errors);
};

FormFieldComponent.prototype.renderReviewErrors = function renderReviewErrors() {
    const errors = [];
    $.each(this.getErrors(), (_, error) => {
        if (error.is_review) {
            errors.push(error);
        }
    });
    this.renderErrors(errors);
};

/**
 * Renders the form field's label.
 * Complex form fields should override this function if needed.
 */
FormFieldComponent.prototype.renderLabel = function renderLabel() {
    if (this.data.label) {
        this.$el.find('.js-control-label').text(this.data.label);
    }
};

/**
 * Renders the form field's value.
 * Complex form fields should override this function if needed.
 */
FormFieldComponent.prototype.renderValue = function renderValue() {
    this.$el.val(this.data.value == null ? undefined : this.data.value);
};

/**
 * Renders the complete form field.
 * Complex form fields should override this function if needed.
 */
FormFieldComponent.prototype.render = function render() {
    if (this.data.choices) {
        this.renderChoices();
    }

    this.$el.prop('disabled', !!this.data.is_readonly);

    if (this.data.label) {
        this.renderLabel();
    }

    this.renderValue();
};

/**
 * Sets the field's data
 *
 * @param {Object} data The data object to set
 */
FormFieldComponent.prototype.setData = function setData(data) {
    this.data = { ...data, name: this.name };
};

/**
 * Sets the error messages for this field
 *
 * @param {Array.<Object>} errors The list of error messages to add to the field
 */
FormFieldComponent.prototype.setErrors = function setErrors(errors) {
    this.data.errors = errors;
};

/**
 * Sets the value for this form field.
 * Complex form fields should override this function if needed.
 *
 * @param {number|string|Array|Object} val The value to set
 */
FormFieldComponent.prototype.setValue = function setValue(val) {
    this.data.value = val;
};

/**
 * Binds actions to the form field element
 */
FormFieldComponent.prototype.bind = function bind() {
    this.$el.on(
        'change',
        $.proxy(function onFieldChange() {
            this.setValue(this.$el.val());
        }, this),
    );
};

export default FormFieldComponent;
